import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from './AuthContext';
import { usePath, useNavigate } from './router';

// Two explicit routes:
//   /           → LoginPage  (login form)
//   /create-user → BootstrapPage (one-shot root creation, gated by secret)
// Backend's /auth/bootstrap refuses if a user already exists, so the
// URL being public isn't a risk — it just returns 409. The split is
// cleaner UX: "forgot your login page" vs "create an admin" are very
// different intents and shouldn't share a surface.

const BOOTSTRAP_PATH = '/create-user';

export const AuthRouter: React.FC = () => {
  const path = usePath();
  if (path === BOOTSTRAP_PATH) return <BootstrapPage />;
  return <LoginPage />;
};

// ═══════════════════════════════════════════════════════════
//  Shared shell — masthead + backdrop + footer
// ═══════════════════════════════════════════════════════════

const AuthShell: React.FC<{ footer: React.ReactNode; children: React.ReactNode }> = ({ footer, children }) => (
  <div className="auth-page">
    <div className="auth-backdrop" aria-hidden />
    <motion.div
      className="auth-shell"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <div className="auth-masthead">
        <div className="auth-volume">VOL. I — ISSUE 01</div>
        <h1 className="auth-title">
          Lato<span className="auth-title-dot">·</span>
        </h1>
        <div className="auth-caption">AI VIRTUAL DEVELOPMENT TEAM</div>
      </div>

      {children}

      <div className="auth-footer">
        <span className="auth-footer-mark">§</span>
        <span>{footer}</span>
      </div>
    </motion.div>
  </div>
);

// ═══════════════════════════════════════════════════════════
//  / — Login
// ═══════════════════════════════════════════════════════════

export const LoginPage: React.FC = () => {
  const { login, bootstrapped } = useAuth();
  const navigate = useNavigate();

  return (
    <AuthShell
      footer="Защищённый доступ. Сессии подписаны HS256 + отзываются на сервере."
    >
      <LoginForm onSubmit={login} />
      {/* If system isn't bootstrapped yet, gently point admins to the
          create-user flow. Not a redirect — user must intend it. */}
      {bootstrapped === false && (
        <div className="auth-inline-link">
          Система ещё не настроена.{' '}
          <a
            href={BOOTSTRAP_PATH}
            onClick={(e) => { e.preventDefault(); navigate(BOOTSTRAP_PATH); }}
          >
            Создать root-учётку →
          </a>
        </div>
      )}
    </AuthShell>
  );
};

// ═══════════════════════════════════════════════════════════
//  /create-user — Bootstrap
// ═══════════════════════════════════════════════════════════

export const BootstrapPage: React.FC = () => {
  const { bootstrap, bootstrapped } = useAuth();
  const navigate = useNavigate();

  return (
    <AuthShell
      footer={
        bootstrapped
          ? 'Root уже создан. Этот роут больше не принимает запросов.'
          : 'Первый запуск. Секрет админа держится в env AUTH_BOOTSTRAP_SECRET.'
      }
    >
      {bootstrapped ? (
        <div className="auth-form-section">
          <div className="auth-section-label">
            <span className="auth-section-num">00</span>
            <span className="auth-section-rule" />
            <span className="auth-section-title">Already bootstrapped</span>
          </div>
          <div className="auth-hint">
            Root-учётка уже существует. Создавать повторно нельзя — сервер
            вернёт 409. Иди на страницу входа.
          </div>
          <div className="auth-actions">
            <button
              className="btn-primary auth-submit"
              onClick={() => navigate('/')}
            >
              На вход →
            </button>
          </div>
        </div>
      ) : (
        <>
          <BootstrapForm onSubmit={bootstrap} />
          <div className="auth-inline-link">
            Уже создали?{' '}
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
            >
              Войти →
            </a>
          </div>
        </>
      )}
    </AuthShell>
  );
};

// ═══════════════════════════════════════════════════════════
//  Login form
// ═══════════════════════════════════════════════════════════

const LoginForm: React.FC<{
  onSubmit: (identifier: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
}> = ({ onSubmit }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier || !password) return;
    setSubmitting(true);
    setError(null);
    const r = await onSubmit(identifier.trim(), password);
    setSubmitting(false);
    if (!r.ok) setError(humanizeError(r.error));
  };

  return (
    <form className="auth-form-section" onSubmit={submit}>
      <div className="auth-section-label">
        <span className="auth-section-num">01</span>
        <span className="auth-section-rule" />
        <span className="auth-section-title">Sign In</span>
      </div>

      <label className="form-label">
        Логин или e-mail
      </label>
      <input
        className="form-input auth-input"
        type="text"
        autoComplete="username"
        autoFocus
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        placeholder="root / root@example.com"
      />

      <label className="form-label" style={{ marginTop: 14 }}>
        Пароль
      </label>
      <input
        className="form-input auth-input"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
      />

      <div className="auth-actions">
        {error && <span className="form-result bad auth-result">{error}</span>}
        <button
          className="btn-primary auth-submit"
          type="submit"
          disabled={submitting || !identifier || !password}
        >
          {submitting ? 'Вход…' : 'Войти'}
        </button>
      </div>
    </form>
  );
};

// ═══════════════════════════════════════════════════════════
//  Bootstrap form (first-run)
// ═══════════════════════════════════════════════════════════

const BootstrapForm: React.FC<{
  onSubmit: (body: { secret: string; username: string; email: string; password: string; displayName?: string }) =>
    Promise<{ ok: true } | { ok: false; error: string }>;
}> = ({ onSubmit }) => {
  const [secret, setSecret] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    secret.trim().length > 0 &&
    username.trim().length >= 3 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    password.length >= 8 &&
    password === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const r = await onSubmit({
      secret: secret.trim(),
      username: username.trim(),
      email: email.trim(),
      password,
      displayName: displayName.trim() || undefined,
    });
    setSubmitting(false);
    if (!r.ok) setError(humanizeError(r.error));
  };

  return (
    <form className="auth-form-section" onSubmit={submit}>
      <div className="auth-section-label">
        <span className="auth-section-num">00</span>
        <span className="auth-section-rule" />
        <span className="auth-section-title">Первый запуск · Root account</span>
      </div>
      <div className="auth-hint">
        Система ещё не настроена. Введи секретное слово администратора и
        создай root-учётку — она получит все пермишены и право раздавать их
        остальным.
      </div>

      <label className="form-label">Секрет администратора</label>
      <input
        className="form-input auth-input"
        type="password"
        autoComplete="off"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        placeholder="секретное слово"
      />

      <label className="form-label" style={{ marginTop: 14 }}>Логин</label>
      <input
        className="form-input auth-input"
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="root"
        autoComplete="username"
      />

      <label className="form-label" style={{ marginTop: 14 }}>E-mail</label>
      <input
        className="form-input auth-input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
      />

      <label className="form-label" style={{ marginTop: 14 }}>
        Имя (опц.)
      </label>
      <input
        className="form-input auth-input"
        type="text"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder="Имя для дашборда"
      />

      <label className="form-label" style={{ marginTop: 14 }}>Пароль (мин. 8 символов)</label>
      <input
        className="form-input auth-input"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        placeholder="••••••••"
      />

      <label className="form-label" style={{ marginTop: 14 }}>Повторите пароль</label>
      <input
        className="form-input auth-input"
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        autoComplete="new-password"
        placeholder="••••••••"
      />

      <div className="auth-actions">
        {error && <span className="form-result bad auth-result">{error}</span>}
        <button
          className="btn-primary auth-submit"
          type="submit"
          disabled={submitting || !canSubmit}
          title={canSubmit ? 'Создать root-учётку' : 'Заполни все поля и совпадающие пароли'}
        >
          {submitting ? 'Создаю…' : 'Создать root'}
        </button>
      </div>
    </form>
  );
};

// Translate backend error codes to user-readable copy. Unknown codes
// fall through as-is so we don't hide server messages.
function humanizeError(code: string): string {
  const map: Record<string, string> = {
    bad_secret: 'Секрет не подходит.',
    already_bootstrapped: 'Root уже создан — используйте вход.',
    invalid_username: 'Логин: только буквы, цифры, _-., 3–32 символа.',
    invalid_email: 'E-mail: неверный формат.',
    invalid_password: 'Пароль: 8–72 символа, без повторяющихся пробелов.',
    invalid_credentials: 'Неверный логин или пароль.',
    user_disabled: 'Учётка отключена. Обратись к админу.',
    bad_request: 'Заполни все поля.',
    unauthorized: 'Сессия закончилась — войди заново.',
    forbidden: 'Нет прав.',
  };
  return map[code] || code;
}

// Tiny utility for consumers of this page to know if auth is ready.
// Keeps this file the single entry-point on the frontend side.
export function useAuthReady(): boolean {
  const { loading, user } = useAuth();
  return !loading && !!user;
}
